import React, { useMemo } from 'react'
import styled from 'styled-components'

// styled
import RdnaAvatar from './index'
import RdnaText from '../RdnaText'
import RdnaTooltip from '../RdnaTooltip'

const DEFAULT_SIZE = 24

const AvatarContainer = styled.div<{ size: number }>`
  display: inline-flex;
  position: relative;
  margin-right: -${({ size }) => (size / DEFAULT_SIZE) * 6}px;
`

type Props = {
  /**
   * Size of avatars
   *
   * @default 24
   * */
  size?: number
  /**
   * Names for each avatar
   * */
  names: string[]
  /**
   * Max number of avatars shown. Every name after that is represented by +i
   *
   * */
  maxShown?: number
  /**
   * Prefix string to go before avatar tooltips
   *
   * */
  tooltipPrefix?: string
}

export default function RdnaAvatarStacked({ size = DEFAULT_SIZE, names, maxShown, tooltipPrefix }: Props) {
  const [shown, extra] = useMemo(() => {
    if (!maxShown || names.length <= maxShown) return [names, []]
    return [names.slice(0, maxShown - 1), names.slice(maxShown - 1)]
  }, [names, maxShown])

  return (
    <>
      {shown.map((name, index) => {
        return (
          <AvatarContainer style={{ zIndex: index }} size={size} key={`${name}-${index}`}>
            <RdnaAvatar name={name} size={size} tooltipText={`${tooltipPrefix || ''}${name}`} />
          </AvatarContainer>
        )
      })}
      {!!extra.length && (
        <AvatarContainer style={{ zIndex: names.length }} size={size}>
          <RdnaTooltip title={extra.join(', ')}>
            <CountCircle size={size}>
              <RdnaText color={'contrast'}>+{extra.length}</RdnaText>
            </CountCircle>
          </RdnaTooltip>
        </AvatarContainer>
      )}
    </>
  )
}

export const CountCircle = styled.div<{ size: number }>`
  background-color: ${({ theme }) => theme.palette.text.link};
  height: ${({ size }) => size}px;
  width: ${({ size }) => size}px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  display: flex;
`
